var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client';
import Button from 'components/button/button';
import { ButtonStyle } from 'components/button/prop-types';
import Loader from 'components/loader/loader';
import RichTextViewer from 'components/rich-text-viewer/rich-text-viewer';
import useAuthentication from 'hooks/use-authentication';
import useVerifyS2BBrowser from 'hooks/use-verify-browser';
import useLogger from 'hooks/use-logger';
import useDateTime from 'hooks/use-date-time';
import { ExamStatus } from 'models/exam-attempt';
import showNotification from 'notifications/notifications';
import EXAM_QUERY from 'queries/get-exam-by-id.graphql';
import EXAM_ATTEMPT_QUERY from 'queries/exam-attempt.graphql';
import START_EXAM_ATTEMPT_MUTATION from 'mutations/start-exam-attempt.graphql';
import { QuestionTypesNames } from 'models/question-type';
const WelcomePage = () => {
    var _a;
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { onS2bBrowser } = useVerifyS2BBrowser();
    const { getUserIdFromToken, getExamIdFromToken } = useAuthentication();
    const logger = useLogger();
    const { currentDateTime, stopInterval } = useDateTime();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isExamAvailable, setIsExamAvailable] = useState(false);
    const { data: dataExam, loading: loadingExam, error: errorExam } = useQuery(EXAM_QUERY, {
        variables: {
            id: getExamIdFromToken(),
        },
    });
    const { data: dataExamAttempt, loading: loadingExamAttempt } = useQuery(EXAM_ATTEMPT_QUERY, {
        variables: {
            userId: getUserIdFromToken(),
            examId: getExamIdFromToken(),
            excludeQuestionTypes: [QuestionTypesNames.MIDDLE_SCREEN]
        },
    });
    const [startAttempt] = useMutation(START_EXAM_ATTEMPT_MUTATION);
    useEffect(() => {
        if (!dataExam) {
            return;
        }
        if (dataExam.exam.isProctored) {
            if (!onS2bBrowser) {
                navigate('/no-s2b');
            }
        }
    }, [dataExam]);
    useEffect(() => {
        var _a, _b;
        if (!dataExam || !currentDateTime || isExamAvailable) {
            return;
        }
        const availableAt = new Date(((_a = dataExam === null || dataExam === void 0 ? void 0 : dataExam.exam) === null || _a === void 0 ? void 0 : _a.availableAt) || '');
        const closingAt = new Date(((_b = dataExam === null || dataExam === void 0 ? void 0 : dataExam.exam) === null || _b === void 0 ? void 0 : _b.closingAt) || '');
        const isAvailable = getIsExamAvailable(availableAt, closingAt);
        setIsExamAvailable(isAvailable);
    }, [currentDateTime]);
    useEffect(() => {
        if (isExamAvailable) {
            stopInterval();
        }
    }, [isExamAvailable]);
    const getButtonTitle = () => {
        if ((dataExamAttempt === null || dataExamAttempt === void 0 ? void 0 : dataExamAttempt.examAttempt.status) === ExamStatus.NOTSTARTED) {
            return t('WELCOME.START_EXAM');
        }
        return t('WELCOME.CONTINUE_EXAM');
    };
    const formatDate = (dateString) => {
        const dateLocalOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        };
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, dateLocalOptions);
    };
    const getIsExamAvailable = (availableAt, closingAt) => {
        if (!currentDateTime) {
            return false;
        }
        return availableAt <= currentDateTime &&
            currentDateTime <= closingAt &&
            (dataExamAttempt === null || dataExamAttempt === void 0 ? void 0 : dataExamAttempt.examAttempt.status) !== ExamStatus.COMPLETED;
    };
    const getTimeLimit = () => {
        if (!dataExam) {
            return '';
        }
        if (dataExam.exam.durationUnit === 'UNLIMITED') {
            return t(`GENERAL.${dataExam.exam.durationUnit}`);
        }
        const translationUnit = t(`GENERAL.${dataExam.exam.durationUnit}`, { count: dataExam.exam.durationTime });
        return `${dataExam.exam.durationTime} ${translationUnit}`;
    };
    const startExam = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsSubmitting(true);
        try {
            // If attempt does not exists create new attempt
            if ((dataExamAttempt === null || dataExamAttempt === void 0 ? void 0 : dataExamAttempt.examAttempt.status) === ExamStatus.NOTSTARTED) {
                yield startAttempt({
                    variables: {
                        userId: getUserIdFromToken(),
                        examId: getExamIdFromToken(),
                    }
                });
            }
            navigate('/take-exam');
        }
        catch (error) {
            logger.error(error);
            showNotification('error', t('WELCOME.START_ATTEMPT_ERROR'));
        }
        setIsSubmitting(false);
    });
    return (_jsxs(_Fragment, { children: [loadingExam && _jsx(Loader, { isFullWith: true }, void 0), errorExam && _jsx("h3", { children: t('GENERAL.ERROR') }, void 0), !loadingExam && !errorExam && dataExam && (_jsxs(_Fragment, { children: [_jsx("h2", { children: dataExam.exam.title }, void 0), _jsx("div", Object.assign({ className: "rich-description__iframe-container" }, { children: _jsx(RichTextViewer, { id: "welcome", content: (_a = dataExam.exam) === null || _a === void 0 ? void 0 : _a.description }, void 0) }), void 0), _jsxs("div", Object.assign({ className: "block--group" }, { children: [_jsxs("div", Object.assign({ className: "block--data text-center" }, { children: [_jsx("i", { className: "fas fa-tasks" }, void 0), _jsx("span", Object.assign({ className: "subtitle", role: "text" }, { children: t('WELCOME.QUANTITY') }), void 0), _jsxs("h4", { children: [dataExam.exam.questions.length, " ", t('GENERAL.QUESTION', { count: dataExam.exam.questions.length })] }, void 0)] }), void 0), _jsxs("div", Object.assign({ className: "block--data text-center" }, { children: [_jsx("i", { className: "fas fa-clock" }, void 0), _jsx("span", Object.assign({ className: "subtitle", role: "text" }, { children: t('WELCOME.TIME_LIMIT') }), void 0), _jsx("h4", { children: getTimeLimit() }, void 0)] }), void 0), _jsxs("div", Object.assign({ className: "block--data text-center" }, { children: [_jsx("i", { className: "fas fa-calendar-alt" }, void 0), _jsx("span", Object.assign({ className: "subtitle" }, { children: t('WELCOME.AVAILABLE_AT') }), void 0), _jsx("h4", { children: formatDate(dataExam.exam.availableAt) }, void 0)] }), void 0), _jsxs("div", Object.assign({ className: "block--data text-center" }, { children: [_jsx("i", { className: "fas fa-calendar-alt" }, void 0), _jsx("span", Object.assign({ className: "subtitle" }, { children: t('WELCOME.DEADLINE') }), void 0), _jsx("h4", { children: formatDate(dataExam.exam.closingAt) }, void 0)] }), void 0)] }), void 0), _jsx("p", { children: t('WELCOME.BYE') }, void 0), _jsx("p", { children: t('WELCOME.INSTRUCTIONS').replace('TIME_LIMIT', getTimeLimit()) }, void 0), !loadingExamAttempt && isExamAvailable && (_jsx(Button, { id: 'starting-button', className: [ButtonStyle.Centered], title: getButtonTitle(), onClick: startExam, loading: isSubmitting }, void 0))] }, void 0))] }, void 0));
};
export default WelcomePage;
